/**
 * StructureRenderModule — renders deployed SWG structures as real 3D models (G1).
 *
 * Resolves each `structure` row's model through the prefab catalog
 * (prefab_model["structure:"+type] → render_asset), loads the GLB via AssetModule,
 * seats it on the terrain (footprint-min terrainSeat at the structure's x,z) at the SWG yaw +
 * catalog scale, and builds a Rapier static collider from the asset's proxy. Because
 * it resolves through the catalog LIVE, re-pointing one prefab_model row + a re-sync
 * swaps the model across every structure of that type.
 *
 * Two render paths (G2), chosen per asset:
 *   • hasLods — a THREE.LOD (the RenderSub culling pass swaps levels by camera
 *     distance). Per-instance LOD swapping needs its own Object3D, so these stay
 *     individual meshes: added to `root` and registered with RenderCullingModule,
 *     unchanged from before.
 *   • no LOD chain — every such placement used to render its OWN `.clone()` of the
 *     shared GLB (one UBO bind per structure per frame — the WebGPURenderer CPU
 *     bottleneck, three.js#30560; the last clone-per-row render path in the client).
 *     This path now batches into ONE shared `StaticInstancer`: every renderable part
 *     of every such structure gets its own instance handle, composed onto the row's
 *     world transform (`collectMeshParts` + a structureId → handles map) — the same
 *     pattern `DecorationModule` uses for structure_item rows. Instanced batches are
 *     `frustumCulled=false` by design (StaticInstancer/InstancedMeshPool), so this
 *     path is no longer registered with RenderCullingModule — the per-clone culling
 *     registration went away along with the per-clone Object3D.
 *
 * Collider registration is unchanged and independent of the render path — every
 * placed structure, LOD or instanced, gets (and on removal loses) its own Rapier
 * static collider keyed by structureId.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { registerBuildingStamps, unregisterBuildingStamps } from '../lib/terrain_stamp.js';
import { ASSET_STORE_BASE } from '../config.js';
import { groundY } from '../lib/grounding.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { setStructureSeat, clearStructureSeat } from '../lib/structure_seat.js';
import { StaticInstancer, collectMeshParts } from '../lib/instancing.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { interiorEligibleDeed, enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import type { RenderCullingModule } from './RenderCullingModule.js';
import { buildLOD } from '../lib/lod.js';
import { enableShadows } from '../lib/shadows.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';

/** Path (a) — LOD-registered structures stay individual meshes (per-instance LOD swap
 *  is incompatible with a static instancer; RenderCullingModule owns their culling). */
/** Per-type render-scale overrides on top of the catalog defaultScale —
 *  the cooked GLBs normalize to ~1.9 m and some deed classes read toy-sized
 *  at the catalog value (user-tuned in-game). Colliders + seating ride the
 *  same final scale. */
/* Probed 2026-08-20 (collider bounds = dequantized truth): every deed GLB
 * normalizes to ~1.9 m on its longest axis — a dollhouse beside the 1.8 m
 * player. Scales below target real-world heights from the MEASURED
 * proportions (e.g. city_hall 1.80 m tall × 15 = 27 m civic monument;
 * small_house 1.47 × 4 = 5.9 m cottage). large_house keeps its earlier
 * in-game-tuned 5. All fit the 64 m structure cell. */
export const TYPE_SCALE: Record<string, number> = {
  merchant_tent: 2,
  small_house: 4,
  medium_house: 6,
  large_house: 10,
  guild_hall: 50, // ×2.5 (2026-08-29): the hall is the town's grandest civic monument
  factory: 12,
  harvester: 7,
  cantina: 9,
  city_hall: 15,
  extractor_small: 3,
  extractor_medium: 4.5,
  extractor_large: 7,
};

interface PlacedLOD { kind: 'lod'; instance: THREE.LOD; assetId: bigint; release: () => void; }
/** Path (b) — non-LOD structures batch into the shared StaticInstancer: one handle per
 *  renderable part of the master GLB, plus the world transform for the `instances` hook
 *  (there's no per-row Object3D left to read position/rotation back from). */
interface PlacedInstanced { kind: 'instanced'; handles: number[]; assetId: bigint; x: number; y: number; z: number; yaw: number; release: () => void; }
type Placed = PlacedLOD | PlacedInstanced;

export class StructureRenderModule implements IModule {
  readonly name = 'StructureRender';
  private scene!: THREE.Scene;
  private readonly root = new THREE.Group();                      // path (a): LOD structures only
  private instancer: StaticInstancer | null = null;
  /** Deed terrain stamps registered per placement (unregistered on remove). */
  private readonly stampAt = new Map<string, { x: number; z: number }>();                // path (b): shared non-LOD structures
  private readonly placed = new Map<string, Placed>();             // structureId → placed model
  private readonly proxyCache = new Map<string, ColliderProxy>();  // assetId → fetched collider proxy
  private subscribed = false;
  private syncing = false;
  private syncDirty = false;

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule, private readonly culling: RenderCullingModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root);
    this.instancer = new StaticInstancer(this.scene, 4096, 'structures');
    this.instancer.setRainLayer(true); // player structures are roofs (render wave 1)
    bus.on('atlas:account-ready', () => { this.subscribe(); void this.sync(); });
    for (const ev of ['atlas:structures-updated', 'atlas:world-entered'] as const) bus.on(ev, () => void this.sync());
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => void this.sync())
      .subscribe([
        'SELECT * FROM structure', 'SELECT * FROM prefab_model', 'SELECT * FROM asset_collider',
        'SELECT * FROM render_asset', 'SELECT * FROM world', 'SELECT * FROM terrain_region',
      ]);
    // 150 ms trailing debounce (doctrine law 4): each event triggers a full
    // O(structures) sync pass — a row drip caused back-to-back full scans.
    let pingTimer: number | null = null;
    const ping = (): void => {
      if (pingTimer !== null) return;
      pingTimer = window.setTimeout(() => {
        pingTimer = null;
        bus.emit('atlas:structures-updated', {});
      }, 150);
    };
    for (const tbl of [conn.db.structure, conn.db.prefabModel]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
    }
  }

  private async sync(): Promise<void> {
    const conn = atlas.conn;
    // A sync request landing MID-SYNC (this loop awaits GLB decodes) must
    // RE-RUN when the current pass ends — dropping it left the previous
    // world's structures AND their colliders resident after a world hop
    // (stale commons/space colliders inside user worlds, 2026-07-30).
    if (this.syncing) { this.syncDirty = true; return; }
    if (!conn || !this.instancer) return;
    this.syncing = true;
    const instancer = this.instancer;
    const instMat = new THREE.Matrix4();
    try {
      const live = new Set<string>();
      // Index the prefab catalog by key ONCE per sync (O(prefabs)) so the per-structure
      // lookup below is O(1) — sync() was O(structures × prefabs): a full prefabModel scan
      // for EACH structure. sync() is event-driven (not per-frame), so one pass is cheap.
      const prefabByKey = new Map([...conn.db.prefabModel.iter()].map((p) => [p.prefabKey, p] as const));
      // Footprint-aware seat (high-relief audit 2026-08-05): the single-point
      // groundY floated a deed house's whole downhill footprint on slopes —
      // seat on the MIN across the scaled footprint like the settlement path.
      let commonsSeed: string | null = null;
      for (const w of conn.db.world.iter()) if (w.kind === 'commons') { commonsSeed = w.terrainSeed; break; }
      const seatAt = (x: number, z: number, footR: number): number =>
        commonsSeed ? terrainSeat(commonsSeed, x, z, Math.max(1, footR), 0.25).y : groundY(conn, x, z);
      const bbScratch = new THREE.Box3();
      let anyNewSeat = false;
      for (const s of conn.db.structure.iter()) {
        if (s.worldId !== atlas.currentWorldId) continue; // render ONLY the world the player is in — else Commons structures bleed into every personal world and never clear on a world hop
        const key = s.structureId.toString();
        const map = prefabByKey.get(`structure:${s.structureType}`);
        if (!map || map.assetId === 0n) continue;
        live.add(key);
        const existing = this.placed.get(key);
        if (existing && existing.assetId === map.assetId) continue; // already correct (transform is static)
        if (existing) this.remove(key); // model re-pointed → reload

        const row = conn.db.renderAsset.renderAssetId.find(map.assetId);
        if (row?.hasLods) {
          // Path (a): a cooked LOD chain → THREE.LOD, culled by RenderCullingModule.
          const h = await buildLOD(this.assets, map.assetId);
          if (this.placed.has(key)) { h.release(); continue; } // concurrent-sync guard
          const inst = h.object;
          bbScratch.setFromObject(inst); // model-space (position/scale applied below)
          const scaleA = (map.defaultScale || 1) * (TYPE_SCALE[s.structureType] ?? 1);
          const footA = Math.max(bbScratch.max.x - bbScratch.min.x, bbScratch.max.z - bbScratch.min.z) / 2 * scaleA;
          const y = seatAt(s.posX, s.posZ, footA * 0.7);
          inst.position.set(s.posX, y, s.posZ);
          inst.rotation.y = s.yaw;
          inst.scale.setScalar(scaleA);
          enableShadows(inst, map.castShadow, true); // G3: structures cast (per the catalog flag) + receive CSM shadows
          this.root.add(inst);
          this.culling.registerLOD(inst);
          this.placed.set(key, { kind: 'lod', instance: inst, assetId: map.assetId, release: h.release });
          // Interior foundation: a player's house/hall is enterable (industry
          // and small deeds are not). LOD-path structures hide by visibility;
          // the collider restores from the cached proxy or the box fallback.
          {
            const bb = new THREE.Box3().setFromObject(inst);
            const bw2 = bb.max.x - bb.min.x, bh2 = bb.max.y - bb.min.y, bd3 = bb.max.z - bb.min.z;
            if (interiorEligibleDeed(s.structureType, bw2, bh2, bd3)) {
              const entId = `deed:${key}`;
              const physics = this.physics;
              const proxyCache = this.proxyCache;
              const aid = map.assetId.toString();
              const colT = { x: s.posX, y, z: s.posZ, yaw: s.yaw, scale: scaleA };
              const boxHalf: [number, number, number] = [bw2 / 2, bh2 / 2, bd3 / 2];
              // Guild hall: the interior door lives on the enterable's local -Z
              // face, but the GLB's carved entrance faces the OTHER way — flip
              // the interior frame π so you walk in through the real archway.
              const entYaw = s.structureType === 'guild_hall' ? s.yaw + Math.PI : s.yaw;
              const ent: EnterableBuilding = {
                id: entId,
                name: s.structureType.replace(/_/g, ' '),
                key: `deed:${s.structureType}`,
                x: s.posX, z: s.posZ, yaw: entYaw, groundY: y,
                w: bw2, h: bh2, d: bd3,
                hide: () => { inst.visible = false; physics.removeStaticCollider(`structure:${key}`); },
                restore: () => {
                  inst.visible = true;
                  const proxy = proxyCache.get(aid);
                  if (proxy) physics.addStaticCollider(`structure:${key}`, proxy, colT);
                  else physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: boxHalf }, { x: s.posX, y: y + bh2 / 2, z: s.posZ, yaw: s.yaw, scale: 1 });
                },
              };
              enterables.set(entId, ent);
              { // terrain flattens under the placed deed structure too
                const st = { x: ent.x, z: ent.z, hw: ent.w / 2 + 0.8, hd: ent.d / 2 + 0.8, yaw: ent.yaw, h: ent.groundY + 0.02 };
                registerBuildingStamps([st]);
                this.stampAt.set(key, { x: st.x, z: st.z });
                bus.emit('atlas:terrain-restamp', { stamps: [st] });
              }
            }
          }
          setStructureSeat(key, { y, x: s.posX, z: s.posZ, r: footA }); anyNewSeat = true; // decor Y + versona keep-out
          void this.addCollider(conn, key, map.assetId, s.posX, y, s.posZ, s.yaw, scaleA, [
            (bbScratch.max.x - bbScratch.min.x) / 2 * scaleA,
            (bbScratch.max.y - bbScratch.min.y) / 2 * scaleA,
            (bbScratch.max.z - bbScratch.min.z) / 2 * scaleA,
          ]);
        } else {
          // Path (b): no LOD chain → the shared StaticInstancer (G2 draw-call collapse).
          // Every renderable part of the master GLB gets its own instance handle,
          // composed onto this structure's world transform — the DecorationModule
          // pattern (collectMeshParts + a row → handles map).
          // Tier-matched load: potato renders the cooked LOD1 (512² textures)
          // — the canonical LOD0 (16×2048² KTX2) was the profiled walk freeze.
          const { object: master, release } = await this.assets.loadForTier(map.assetId);
          if (this.placed.has(key)) { release(); continue; } // concurrent-sync guard
          const scale = (map.defaultScale || 1) * (TYPE_SCALE[s.structureType] ?? 1);
          bbScratch.setFromObject(master);
          const footB = Math.max(bbScratch.max.x - bbScratch.min.x, bbScratch.max.z - bbScratch.min.z) / 2 * scale;
          const y = seatAt(s.posX, s.posZ, footB * 0.7);
          const placeMat = new THREE.Matrix4().compose(
            new THREE.Vector3(s.posX, y, s.posZ),
            new THREE.Quaternion().setFromEuler(new THREE.Euler(0, s.yaw, 0)),
            new THREE.Vector3(scale, scale, scale),
          );
          const handles: number[] = [];
          for (const part of collectMeshParts(master)) {
            const id = instancer.add(part.geometry, part.material, instMat.multiplyMatrices(placeMat, part.local));
            if (id >= 0) {
              handles.push(id);
              // StaticInstancer doesn't hand back the InstancedMesh it just created/reused
              // for this (geometry, material) batch, so the catalog's per-prefab castShadow
              // flag (G3) can't be applied the way enableShadows(clone, ...) used to — locate
              // the batch (InstancedMeshPool.meshFor) and flip the SAME
              // flags there instead (via the pool — the batch hangs under the anchor group).
              this.applyBatchShadow(part.geometry, part.material, map.castShadow, true);
            }
          }
          this.placed.set(key, { kind: 'instanced', handles, assetId: map.assetId, x: s.posX, y, z: s.posZ, yaw: s.yaw, release });
          setStructureSeat(key, { y, x: s.posX, z: s.posZ, r: footB }); anyNewSeat = true; // decor Y + versona keep-out
          void this.addCollider(conn, key, map.assetId, s.posX, y, s.posZ, s.yaw, scale, [
            (bbScratch.max.x - bbScratch.min.x) / 2 * scale,
            (bbScratch.max.y - bbScratch.min.y) / 2 * scale,
            (bbScratch.max.z - bbScratch.min.z) / 2 * scale,
          ]);
        }
      }
      for (const key of [...this.placed.keys()]) if (!live.has(key)) this.remove(key);
      // New seats registered → ping decor to re-compose on the exact parent Y.
      // Terminates: the re-entrant structure pass places nothing new (all rows
      // already match) so it never re-emits.
      if (anyNewSeat) bus.emit('atlas:structures-updated', {});
    } finally {
      this.syncing = false;
      if (this.syncDirty) { this.syncDirty = false; void this.sync(); }
    }
  }

  /** Force cast/receive shadow flags on the InstancedMesh batch a (geometry, material)
   *  pair maps to (path b only — path a keeps calling `enableShadows` directly). */
  private applyBatchShadow(geometry: THREE.BufferGeometry, material: THREE.Material, cast: boolean, receive: boolean): void {
    // The batch lives under the instancer's ANCHOR group since the 2026-08-23
    // precision fix — a scene.children scan no longer finds it; ask the pool.
    const m = this.instancer?.meshFor(geometry, material);
    if (m) enableShadows(m as THREE.Object3D, cast, receive);
  }

  private async addCollider(conn: DbConnection, key: string, assetId: bigint, x: number, y: number, z: number, yaw: number, scale: number, halfExtents?: [number, number, number]): Promise<void> {
    const col = conn.db.assetCollider.assetId.find(assetId);
    if (!col) {
      // No cooked proxy → AABB box fallback (high-relief audit 2026-08-05):
      // a proxy-less deed house was a walk-through ghost. Half-extents arrive
      // PRE-SCALED (scaleProxy does not scale a box → pass scale 1).
      if (halfExtents && this.placed.has(key)) {
        const [hx, hy, hz] = halfExtents;
        this.physics.addStaticCollider(`structure:${key}`, { shape: 'box', halfExtents: [hx, hy, hz] }, { x, y: y + hy, z, yaw, scale: 1 });
      }
      return;
    }
    let proxy = this.proxyCache.get(assetId.toString());
    if (!proxy) {
      try { proxy = (await (await fetch(`${ASSET_STORE_BASE}/${col.colliderUri}`)).json()) as ColliderProxy; this.proxyCache.set(assetId.toString(), proxy); }
      catch { return; }
    }
    if (this.placed.has(key)) this.physics.addStaticCollider(`structure:${key}`, proxy, { x, y, z, yaw, scale });
  }

  private remove(key: string): void {
    const p = this.placed.get(key);
    if (!p) return;
    if (p.kind === 'lod') {
      this.root.remove(p.instance);
      this.culling.unregisterLOD(p.instance);
      p.release();
    } else {
      for (const id of p.handles) this.instancer?.remove(id);
      p.release(); // release AFTER instances are gone (matched to the tier-picked cache key)
    }
    const st = this.stampAt.get(key);
    if (st) { unregisterBuildingStamps(st.x, st.z); this.stampAt.delete(key); }
    this.physics.removeStaticCollider(`structure:${key}`); // collider teardown is independent of the render representation — always runs
    enterables.delete(`deed:${key}`);
    clearStructureSeat(key);
    this.placed.delete(key);
  }

  /** Test/inspection hook: how many structures are rendered. */
  get count(): number { return this.placed.size; }

  /** Test/inspection hook: each placed instance's world transform + bound asset. */
  get instances(): { x: number; y: number; z: number; yaw: number; assetId: string }[] {
    return [...this.placed.values()].map((p) => p.kind === 'lod'
      ? { x: p.instance.position.x, y: p.instance.position.y, z: p.instance.position.z, yaw: p.instance.rotation.y, assetId: p.assetId.toString() }
      : { x: p.x, y: p.y, z: p.z, yaw: p.yaw, assetId: p.assetId.toString() });
  }
}
